import React, { useEffect, useState } from "react";
import { expensesApi, accountsApi } from "../api";
import { getTimeframe, toIsoRange } from "../timeframe";
import ExpenseModal from "./ExpenseModal";
import { useI18n } from "../i18n";
import SubscriptionBanner from "./SubscriptionBanner";
import { useAccount } from "../contexts/AccountContext";
import { usePermissions } from "../contexts/PermissionContext";
import { useOfflineOperations } from "../hooks/useOfflineOperations";
import { showConfirm } from "../utils/toast";

const API_BASE = process.env.REACT_APP_API_BASE;

async function request(method, path, body) {
  const headers = { "Content-Type": "application/json" };
  const token = localStorage.getItem("authToken") || localStorage.getItem("token") || "";
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export default function Expenses({ user, subStatus, onSubscribe, onRefreshSubscription }) {
  const { t } = useI18n();
  const { selectedAccountId } = useAccount();
  const { hasPermission } = usePermissions();
  const { canPerformOperation, showOfflineWarning, message: offlineMessage } = useOfflineOperations('expenses');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().split("T")[0];
  const [draft, setDraft] = useState({ date: today, amount: "", category: "Other", frequency: "daily", note: "" });
  const [editing, setEditing] = useState(null); // expense being edited
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [editDraft, setEditDraft] = useState({ date: today, amount: "", category: "Other", frequency: "daily", note: "", money_account_id: "" });
  const [categories, setCategories] = useState(["Other", "Rent", "Electricity", "Wages", "Delivery", "Marketing"]);
  const [accounts, setAccounts] = useState([]);
  const [selectedMoneyAccountId, setSelectedMoneyAccountId] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [openNoteId, setOpenNoteId] = useState(null);
  const [truncatedMap, setTruncatedMap] = useState({});
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchQ, setSearchQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      // Always fetch based on global timeframe only
      const tf = getTimeframe();
      const iso = toIsoRange(tf);
      if (iso.from && iso.to) { params.from = iso.from; params.to = iso.to; }
      if (selectedAccountId) {
        params.accountId = selectedAccountId;
      }
      const data = await expensesApi.list(params);
      setList(data || []);
    } catch (e) {
      console.error("Failed to load expenses", e);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => { load(); }, [selectedAccountId]);

  // React to global timeframe changes
  useEffect(() => {
    const handler = () => { load(); };
    window.addEventListener('TP_TIMEFRAME_CHANGE', handler);
    return () => window.removeEventListener('TP_TIMEFRAME_CHANGE', handler);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [cats, accs] = await Promise.all([
          expensesApi.listCategories(),
          accountsApi.list().catch(() => []),
        ]);
        if (Array.isArray(cats) && cats.length) setCategories(cats);
        else if (cats && typeof cats === 'object') {
          const defs = Array.isArray(cats.defaults) ? cats.defaults : [];
          const customs = Array.isArray(cats.customs) ? cats.customs : [];
          const merged = Array.from(new Set([...defs, ...customs]));
          if (merged.length) setCategories(merged);
        }
        setAccounts(Array.isArray(accs) ? accs : []);
        try {
          const last = sessionStorage.getItem('tp_selected_account_id') || '';
          if (last && (accs || []).some(a => a.accountId === last)) setSelectedMoneyAccountId(last);
          else setSelectedMoneyAccountId("");
        } catch { setSelectedMoneyAccountId(""); }
      } catch {}
    })();
  }, []);

  const handleAdd = async () => { setEditingExpenseId(null); setShowExpenseModal(true); };

  const openEdit = (e) => {
    const dateStr = new Date(e.date?._seconds? e.date._seconds*1000 : e.date).toISOString().split('T')[0];
    setEditing(e);
    setEditingExpenseId(e.expenseId);
    setShowExpenseModal(true);
    setEditDraft({
      date: dateStr,
      amount: String(e.amount || ''),
      category: e.category || 'Other',
      frequency: (e.frequency || 'daily'),
      note: e.note || '',
      money_account_id: e.money_account_id || '',
    });
    setSelectedMoneyAccountId(e.money_account_id || "");
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await expensesApi.update(editing.expenseId, {
        date: editDraft.date,
        amount: parseFloat(editDraft.amount || 0),
        category: editDraft.category,
        frequency: editDraft.frequency,
        note: editDraft.note,
        money_account_id: editDraft.money_account_id === '' ? null : (editDraft.money_account_id || undefined),
      });
      try { if (window && window.gtag) window.gtag('event', 'expense_update', { expense_id: editing.expenseId }); } catch {}
      try {
        const accToRecon = editDraft.money_account_id || selectedAccountId || '';
        if (accToRecon) await accountsApi.reconcile(accToRecon);
      } catch {}
      setEditing(null);
      await load();
      // Trigger P&L refresh if the tab is open by dispatching a visibility event
      document.dispatchEvent(new Event('visibilitychange'));
      // Refresh subscription status to update usage
      if (onRefreshSubscription) {
        await onRefreshSubscription();
      }
    } catch (err) {
      console.error('Failed to update expense', err);
    }
  };

  const closeEdit = () => setEditing(null);

  const handleDelete = async (e) => {
    const confirmed = await showConfirm({
      title: t('exp.confirm.delete'),
      message: t('exp.confirm.delete'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      destructive: true,
      variant: 'warning',
    });
    if (!confirmed) return;
    try {
      await expensesApi.remove(e.expenseId);
      try { if (window && window.gtag) window.gtag('event', 'expense_delete', { expense_id: e.expenseId }); } catch {}
      await load();
      document.dispatchEvent(new Event('visibilitychange'));
      // Refresh subscription status to update usage
      if (onRefreshSubscription) {
        await onRefreshSubscription();
      }
    } catch (err) {
      console.error('Failed to delete expense', err);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 p-4 font-sans">
      {/* Offline Warning Banner */}
      {showOfflineWarning && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-yellow-600">⚠️</span>
            <div>
              <div className="font-medium text-yellow-800">Offline Mode</div>
              <div className="text-sm text-yellow-700">{offlineMessage}</div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="text-lg font-bold">{t('exp.title')}</div>
        {hasPermission('expenses:create') && (
          <button 
            data-tour="add-expense" 
            className="text-sm bg-green-600 text-white px-3 py-2 rounded-full shadow" 
            onClick={() => {
              // Check if subscription is inactive
              if (subStatus?.valid === false) {
                onSubscribe?.();
                return;
              }
              // Check if trial user has exceeded quota
              if (subStatus?.plan === 'trial' && subStatus?.quotaInfo?.quotaExceeded) {
                onSubscribe?.();
                return;
              }
              handleAdd();
            }}
          >
            + {t('exp.add.addExpense')}
          </button>
        )}
      </div>


      <SubscriptionBanner 
        user={user} 
        subStatus={subStatus} 
        onSubscribe={onSubscribe}
        showCreateButton={true}
        quotaInfo={subStatus?.quotaInfo}
      />
      {/* Filters: Search, Date and Category (styled like Orders) */}
      <div className="bg-white border border-green-100 rounded-2xl p-3 shadow-sm mb-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 items-start">
          <div className="col-span-1 sm:col-span-2 md:col-span-2 min-w-0">
            <div className="text-[11px] text-gray-600 mb-1 h-[14px]">Search notes or category</div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔎</span>
              <input
                className="w-full border rounded-full pl-8 pr-10 py-2 text-sm bg-green-50/60 focus:bg-white"
                placeholder="Search notes or category"
                value={searchQ}
                onChange={(e)=> setSearchQ(e.target.value)}
                aria-label="Search notes or category"
              />
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-gray-600 mb-1 h-[14px]">{t('orders.filter.from')}</div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📅</span>
              <input
                type="date"
                className="w-full border rounded-full pl-8 pr-3 py-2 text-sm bg-green-50/60 focus:bg-white"
                value={fromDate}
                onChange={(e)=>{ const v = e.target.value; setFromDate(v); }}
                aria-label={t('orders.filter.from')}
              />
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-gray-600 mb-1 h-[14px]">{t('orders.filter.to')}</div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📅</span>
              <input
                type="date"
                className="w-full border rounded-full pl-8 pr-3 py-2 text-sm bg-green-50/60 focus:bg-white"
                value={toDate}
                onChange={(e)=>{ const v = e.target.value; setToDate(v); }}
                aria-label={t('orders.filter.to')}
              />
            </div>
          </div>
          <div className="sm:col-span-2 md:col-span-1 min-w-0 flex flex-col justify-end">
            <div className="text-[11px] text-gray-600 mb-1 h-[14px]">Filter by category</div>
            <div className="flex gap-2">
              <select className="flex-1 border rounded-full px-3 py-2 text-sm bg-green-50/60 focus:bg-white text-gray-700" value={catFilter} onChange={(e)=>setCatFilter(e.target.value)}>
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <button
                type="button"
                className="w-full sm:w-auto text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full px-3 py-2"
                onClick={()=>{ setSearchQ(""); setFromDate(""); setToDate(""); setCatFilter(""); load(); }}
                aria-label={t('orders.filter.clear')}
                title={t('orders.filter.clear')}
              >
                <span className="mr-1">✕</span>{t('orders.filter.clear')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Range pill (placed after filters) */}
      <div className="mb-2">
        {(() => { const tf = getTimeframe(); const fromDisp = fromDate || tf.from; const toDisp = toDate || tf.to; return (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-100 shadow-sm text-[11px] text-gray-700">
            <span>Range:</span>
            <span className="font-semibold">{fromDisp} → {toDisp}</span>
          </div>
        ); })()}
      </div>

      {/* Categories management */}
      <div className="bg-white border border-green-100 rounded-2xl p-3 shadow-sm mb-3">
        <div className="grid sm:grid-cols-3 gap-2 items-end">
          <div>
            <div className="text-xs text-gray-600 mb-1">Create custom category</div>
            <div className="flex gap-2">
              <input className="border rounded px-2 py-1 text-sm flex-1" placeholder="e.g. Internet" value={newCategory} onChange={(e)=>setNewCategory(e.target.value)} />
              <button className="px-3 py-1.5 rounded bg-green-600 text-white text-sm" onClick={async ()=>{
                const name = (newCategory || '').trim();
                if (!name) return;
                try {
                  await expensesApi.createCategory(name);
                  const next = await expensesApi.listCategories();
                  if (Array.isArray(next) && next.length) setCategories(next);
                  setNewCategory('');
                } catch {}
              }}>Add</button>
            </div>
          </div>
          <div className="sm:col-span-2"></div>
        </div>
      </div>

      <div className="bg-white border border-green-100 rounded-2xl p-3 shadow-sm">
        <div className="text-sm font-semibold mb-2">{t('exp.recent.title')}</div>
        {loading ? (
          <div className="text-xs text-gray-500">{t('exp.recent.loading')}</div>
        ) : (
          <div className="space-y-2">
            {list
              .filter((e)=>{
                // Date filters
                const d = new Date(e.date?._seconds? e.date._seconds*1000 : e.date || Date.now());
                const ts = d.setHours(0,0,0,0);
                const fromTs = fromDate ? new Date(fromDate).setHours(0,0,0,0) : null;
                const toTs = toDate ? new Date(toDate).setHours(23,59,59,999) : null;
                if (fromTs!=null && ts < fromTs) return false;
                if (toTs!=null && ts > toTs) return false;
                // Category filter
                if (catFilter && String(e.category || '') !== catFilter) return false;
                // Search filter on note and category
                if (searchQ) {
                  const q = searchQ.toLowerCase();
                  const note = String(e.note || '').toLowerCase();
                  const cat = String(e.category || '').toLowerCase();
                  if (!note.includes(q) && !cat.includes(q)) return false;
                }
                return true;
              })
              .map((e) => {
              const isOpen = openNoteId === e.expenseId;
              const noteText = String(e.note || '');
              const isTruncated = !!truncatedMap[e.expenseId];
              return (
                <div key={e.expenseId} className="text-sm border-b last:border-b-0 pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800">{e.category} <span className="text-xs text-gray-400">({e.frequency || 'daily'})</span></div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 min-w-0">
                        <span>{new Date(e.date?._seconds? e.date._seconds*1000 : e.date || Date.now()).toLocaleDateString()}</span>
                        {noteText ? (
                          <>
                            <span>·</span>
                            <span
                              className="inline-block truncate max-w-[200px] align-bottom"
                              title={noteText}
                              ref={(el) => {
                                if (el) {
                                  const t = el.scrollWidth > el.clientWidth;
                                  setTruncatedMap((s) => (s[e.expenseId] === t ? s : { ...s, [e.expenseId]: t }));
                                }
                              }}
                            >{noteText}</span>
                            {isTruncated && (
                              <button className="text-[11px] text-green-600 underline" onClick={()=> setOpenNoteId(isOpen ? null : e.expenseId)}>
                                {isOpen ? 'Hide' : 'View'}
                              </button>
                            )}
                          </>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-2">
                      <div className="text-green-700 font-semibold">₹{Number(e.amount || 0).toFixed(0)}</div>
                      {hasPermission('expenses:edit') && (
                        <button className="text-xs text-green-600 underline" onClick={() => openEdit(e)}>{t('exp.action.edit')}</button>
                      )}
                      {hasPermission('expenses:delete') && (
                        <button className="text-xs text-red-600 underline" onClick={() => handleDelete(e)}>{t('exp.action.delete')}</button>
                      )}
                    </div>
                  </div>
                  {isOpen && noteText ? (
                    <div className="mt-1 text-[11px] text-gray-700 bg-green-50 border border-green-100 rounded p-2 whitespace-pre-wrap break-words">
                      {noteText}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {list.length === 0 && (
              <div className="flex items-center justify-between bg-green-50 border border-green-100 rounded-xl p-3">
                <div className="text-xs text-gray-700">{t('exp.recent.empty')}</div>
                {hasPermission('expenses:create') && (
                  <button 
                    data-tour="add-expense" 
                    className="text-sm bg-green-600 text-white px-3 py-2 rounded-full shadow" 
                    onClick={() => {
                      // Check if subscription is inactive
                      if (subStatus?.valid === false) {
                        onSubscribe?.();
                        return;
                      }
                      // Check if trial user has exceeded quota
                      if (subStatus?.plan === 'trial' && subStatus?.quotaInfo?.quotaExceeded) {
                        onSubscribe?.();
                        return;
                      }
                      handleAdd();
                    }}
                  >
                    + {t('exp.add.addExpense')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <ExpenseModal 
        open={!!showExpenseModal} 
        expenseId={editingExpenseId} 
        onClose={async (changed)=>{ setShowExpenseModal(false); setEditingExpenseId(null); setEditing(null); if (changed) { await load(); if (onRefreshSubscription) await onRefreshSubscription(); } }} 
      />
    </div>
  );
}


